import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import { ChoiceRow } from '@/components/molecules/ChoiceRow';
import { useExpandedState } from '@/hooks/useExpandedState';
import { MoveCard } from '@/components/molecules/MoveCard';
import { findMoveByIdAnyScope, findMoveByIdGlobal } from '@/data/moves';
import { dwrClassesById } from '@/data/classes';
import { renderInlineMarkdown } from '@/utils/inlineMarkdown';
import type { ClassId } from '@/schemas/classId';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { getGrantedPool } from '@/services/moveGrants';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../../services/moveRefMutations';
import type { LevelUpGrantedChoiceDraft } from './types';

// page-specific (Fase 2 PR 9): step «Sotto-scelta» del flusso di salita
// di livello (D5 — FI-31). Pixel-parity con pattern shell PR 8:
// <SectionHeader> + body card-driven, no bottoni interni (delegati a
// <StepNav> esterno renderizzato da LevelUpFlowPage).
//
// Visibile solo se la mossa appena scelta ha `grantsMoveChoice`. 3 kinds:
//
//  - `from-group`: opzioni = mosse del gruppo iniziale non ancora prese.
//    Render con `<MoveCard>` (description espandibile via chevron).
//    Se l'opzione selezionata ha `hasMoveSetup`, attiva il `MoveSetupEditor`.
//
//  - `from-choice-move`: opzioni = `MoveChoice` della mossa-config sorgente
//    non ancora selezionate. Render con `<ChoiceRow>` (label + description).
//    Nessun setup annidato (le choice sono primitive: ID + label).
//
//  - `from-choice-mapping`: opzioni = mosse cross-class predeterminate.
//    Render con `<MoveCard>` + label "(Classe)" per chiarezza.
//
// **D5-Q8 UX**: pool=1 → auto-select al primo render.
//
// **PR 9 cambio API**: prop `onNext` / `onBack` rimosse.

interface LevelUpStepGrantedProps {
  character: Character;
  classDef: ClassDefinition;
  moveDef: Move;
  granted: LevelUpGrantedChoiceDraft | undefined;
  onGrantedChange: (granted: LevelUpGrantedChoiceDraft | undefined) => void;
}

export function LevelUpStepGranted(props: LevelUpStepGrantedProps) {
  const { character, classDef, moveDef, granted, onGrantedChange } = props;
  const grant = moveDef.grantsMoveChoice;

  const pool = useMemo<readonly string[]>(() => {
    if (!grant) return [];
    return getGrantedPool(character, grant, classDef);
  }, [character, grant, classDef]);

  // D5-Q8: auto-select su pool=1.
  useEffect(() => {
    const onlyOption = pool[0];
    if (pool.length === 1 && onlyOption !== undefined && granted === undefined) {
      onGrantedChange({ selectedOptionId: onlyOption });
    }
  }, [pool, granted, onGrantedChange]);

  // FI-79: ownedMoveIds del PG per disabilitare spell-options con
  // `requiresMove` non soddisfatto. Coerente con builder L1 + LevelUpStepMove.
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);

  // FI-75: setup pannello inline alla MoveCard. Visibile solo per la mossa
  // selezionata (granted.selectedOptionId === optionId) e solo per i kind
  // che supportano setup (`from-group`, `from-choice-mapping`).
  // `from-choice-move` non passa di qui (è renderizzato con ChoiceRow).
  const renderSetupSlot = useCallback(
    (move: Move | undefined, optionId: string): ReactNode => {
      if (!move || !hasMoveSetup(move)) return null;
      if (granted?.selectedOptionId !== optionId) return null;
      const setup = granted.selectedMoveSetup;
      const moveRef: MoveRef = setup
        ? { id: optionId, ...setup }
        : { id: optionId, selectedChoices: [] };
      const onSetupChange = (next: Omit<MoveRef, 'id'> | undefined): void => {
        onGrantedChange(
          next
            ? { selectedOptionId: optionId, selectedMoveSetup: next }
            : { selectedOptionId: optionId },
        );
      };
      return (
        <MoveSetupPanel
          move={move}
          selectedByGroup={buildSelectedByGroup(move, moveRef)}
          narrativeValues={moveRef.narrativeFieldValues ?? {}}
          recruitOutcome={moveRef.recruitOutcome}
          archetypeId={character.archetypeId}
          ownedMoveIds={ownedMoveIds}
          onChoiceGroupChange={(_moveId, groupId, selectedIds) => {
            onSetupChange(updateSetupChoiceGroup(moveRef, groupId, selectedIds));
          }}
          onNarrativeFieldChange={(_moveId, fieldId, value) => {
            onSetupChange(updateSetupNarrativeField(moveRef, fieldId, value));
          }}
        />
      );
    },
    [granted, character.archetypeId, ownedMoveIds, onGrantedChange],
  );

  if (!grant) return null;

  return (
    <section>
      <SectionHeader title={`Sotto-scelta da «${moveDef.name}»`} sub={describeGrant(moveDef)} />
      <div className="mt-4 space-y-4">
        <GrantedPoolList
          grant={grant}
          pool={pool}
          classDef={classDef}
          granted={granted}
          onSelect={(selectedOptionId) => onGrantedChange({ selectedOptionId })}
          renderSetupSlot={renderSetupSlot}
        />
      </div>
    </section>
  );
}

/**
 * Proietta un `MoveRef` in `selectedByGroup` per `MoveSetupPanel`.
 * Stesso pattern di `LevelUpStepMove.buildSelectedByGroup`.
 */
function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function describeGrant(moveDef: Move): string {
  const grant = moveDef.grantsMoveChoice;
  if (!grant) return '';
  if (grant.kind === 'from-group') {
    return `«${moveDef.name}» concede una mossa residua del gruppo iniziale della classe.`;
  }
  if (grant.kind === 'from-choice-move') {
    return `«${moveDef.name}» concede un'opzione residua dalle scelte di una mossa già presa.`;
  }
  // FI-30 (D6): from-choice-mapping
  return `«${moveDef.name}» concede una mossa cross-class predeterminata in base alla scelta fatta in una mossa-config della tua classe.`;
}

interface GrantedPoolListProps {
  grant: NonNullable<Move['grantsMoveChoice']>;
  pool: readonly string[];
  classDef: ClassDefinition;
  granted: LevelUpGrantedChoiceDraft | undefined;
  onSelect: (optionId: string) => void;
  renderSetupSlot: (move: Move | undefined, optionId: string) => ReactNode;
}

function GrantedPoolList({
  grant,
  pool,
  classDef,
  granted,
  onSelect,
  renderSetupSlot,
}: GrantedPoolListProps) {
  if (pool.length === 0) {
    return (
      <p className="text-ink-soft font-serif text-[0.92rem] italic">
        Nessuna opzione residua disponibile. Torna indietro e scegli un'altra mossa.
      </p>
    );
  }
  if (grant.kind === 'from-choice-move') {
    return (
      <ChoicePoolList
        grant={grant}
        pool={pool}
        classDef={classDef}
        granted={granted}
        onSelect={onSelect}
        renderSetupSlot={renderSetupSlot}
      />
    );
  }
  return (
    <MovePoolList
      grant={grant}
      pool={pool}
      classDef={classDef}
      granted={granted}
      onSelect={onSelect}
      renderSetupSlot={renderSetupSlot}
    />
  );
}

// `from-choice-move`: le opzioni sono `MoveChoice` primitive (label + desc),
// non mosse intere → `<ChoiceRow>` (PR 4) semantico. `renderSetupSlot` non
// usato (le choice non hanno setup), accettato per uniformità di signature.
function ChoicePoolList({
  grant,
  pool,
  classDef,
  granted,
  onSelect,
  renderSetupSlot: _renderSetupSlot,
}: GrantedPoolListProps) {
  if (grant.kind !== 'from-choice-move') return null;
  return (
    <div className="flex flex-col gap-2">
      {pool.map((optionId) => {
        const info = lookupChoiceOptionInfo(grant, optionId, classDef);
        const isSelected = granted?.selectedOptionId === optionId;
        const description =
          info.description !== undefined ? renderInlineMarkdown(info.description) : undefined;
        return (
          <ChoiceRow
            key={optionId}
            title={info.label}
            {...(description !== undefined ? { description } : {})}
            selected={isSelected}
            onClick={() => onSelect(optionId)}
          />
        );
      })}
    </div>
  );
}

// `from-group` + `from-choice-mapping`: le opzioni sono mosse intere
// → `<MoveCard>` (PR 6+8) per coerenza con `LevelUpStepMove`.
// classDef non usato qui (lookup nome via findMoveByIdGlobal cross-class),
// ma mantenuto in signature per uniformità con `ChoicePoolList`.
function MovePoolList({
  grant,
  pool,
  granted,
  onSelect,
  classDef: _classDef,
  renderSetupSlot,
}: GrantedPoolListProps) {
  const expandState = useExpandedState(pool);
  const { expanded, toggleExpanded } = expandState;
  return (
    <RadioGroup value={granted?.selectedOptionId ?? ''} className="gap-2">
      {pool.map((optionId) => {
        const move = findMoveByIdGlobal(optionId);
        const inputId = `granted-${optionId}`;
        const isSelected = granted?.selectedOptionId === optionId;
        const crossClassLabel = getCrossClassLabel(grant, move);
        return (
          <MoveCard
            key={optionId}
            move={move}
            fallbackId={optionId}
            expanded={expanded.has(optionId)}
            onToggle={() => toggleExpanded(optionId)}
            labelHtmlFor={inputId}
            selected={isSelected}
            leading={
              <RadioGroupItem
                value={optionId}
                id={inputId}
                className="mt-1"
                onClick={() => onSelect(optionId)}
              />
            }
            {...(crossClassLabel ? { trailing: <CrossClassLabel text={crossClassLabel} /> } : {})}
            setupSlot={isSelected ? renderSetupSlot(move, optionId) : null}
          />
        );
      })}
    </RadioGroup>
  );
}

function CrossClassLabel({ text }: { text: string }) {
  return (
    <span className="bg-ink-soft text-paper-bright font-display inline-block px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] uppercase">
      {text}
    </span>
  );
}

/**
 * Per `from-choice-mapping` (cross-class): label nome classe sorgente
 * della mossa. Per `from-group` (same-class) → undefined (no badge,
 * la mossa è della classe del PG).
 */
function getCrossClassLabel(
  grant: NonNullable<Move['grantsMoveChoice']>,
  move: Move | undefined,
): string | undefined {
  if (grant.kind !== 'from-choice-mapping') return undefined;
  if (!move) return undefined;
  const def = move.classId !== undefined ? dwrClassesById[move.classId as ClassId] : undefined;
  return def?.name;
}

function lookupChoiceOptionInfo(
  grant: NonNullable<Move['grantsMoveChoice']> & { kind: 'from-choice-move' },
  optionId: string,
  classDef: ClassDefinition,
): { label: string; description: string | undefined } {
  const sourceMove = findMoveByIdAnyScope(classDef.id, grant.moveId);
  const choice = sourceMove?.choices?.find((c) => c.id === optionId);
  if (!choice) return { label: optionId, description: undefined };
  return { label: choice.label, description: choice.description };
}

function updateSetupChoiceGroup(
  moveRef: MoveRef,
  groupId: string,
  selectedIds: string[],
): Omit<MoveRef, 'id'> {
  return stripId(updateMoveChoiceGroup(moveRef, moveRef.id, groupId, selectedIds));
}

function updateSetupNarrativeField(
  moveRef: MoveRef,
  fieldId: string,
  value: string,
): Omit<MoveRef, 'id'> {
  return stripId(updateMoveNarrativeField(moveRef, moveRef.id, fieldId, value));
}

function stripId(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const { id: _id, ...rest } = moveRef;
  return rest;
}
